//src/components/admin/BlogManagement.tsx
import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Pencil, Trash2, Eye, Search, Filter, Calendar, 
  Clock, Tag, X, Star, Save, RefreshCw, MoreVertical, Image,
  Globe, Users, TrendingUp, CheckCircle, AlertCircle, Edit3,
  Archive, Copy, Share, BarChart3, BookOpen, Zap
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  featured: boolean;
  read_time: string;
  content: string;
  keywords: string[];
  published: boolean;
  created_at: string;
  updated_at: string;
}

interface BlogStats {
  totalPosts: number;
  publishedPosts: number;
  draftPosts: number;
  featuredPosts: number;
  categoryCounts: { [key: string]: number };
}

const CATEGORIES = [
  'Industry Insights',
  'Government Contracting',
  'Proposal Writing',
  'Business Development',
  'Technology',
  'Company News',
  'How-To Guides',
  'Case Studies'
];

const CONTENT_TEMPLATE = `# Your Article Title

## Introduction

Write a compelling introduction that hooks your readers and clearly states what they'll learn from this article.

## Main Content

### Key Point 1
Explain your first main point with details, examples, and actionable insights.

### Key Point 2
Continue with your second main point, providing value to your readers.

### Key Point 3
Add more sections as needed to fully cover your topic.

## Conclusion

Summarize the key takeaways and provide a clear call-to-action for your readers.

---

**Pro tip:** Use bullet points, numbered lists, and bold text to make your content scannable and engaging.`;

export function BlogManagement() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [stats, setStats] = useState<BlogStats>({
    totalPosts: 0,
    publishedPosts: 0,
    draftPosts: 0,
    featuredPosts: 0,
    categoryCounts: {}
  });
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    author: 'BidWin by SerVet Digital',
    date: new Date().toISOString().split('T')[0],
    category: 'Industry Insights',
    featured: false,
    read_time: '5 min read',
    content: CONTENT_TEMPLATE,
    keywords: [] as string[],
    published: false
  });

  useEffect(() => {
    loadPosts();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [posts]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error loading blog posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const totalPosts = posts.length;
    const publishedPosts = posts.filter(post => post.published).length;
    const draftPosts = posts.filter(post => !post.published).length;
    const featuredPosts = posts.filter(post => post.featured).length;
    
    const categoryCounts = posts.reduce((acc, post) => {
      acc[post.category] = (acc[post.category] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    setStats({
      totalPosts,
      publishedPosts,
      draftPosts,
      featuredPosts,
      categoryCounts
    });
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      excerpt: '',
      author: 'BidWin by SerVet Digital',
      date: new Date().toISOString().split('T')[0],
      category: 'Industry Insights',
      featured: false,
      read_time: '5 min read',
      content: CONTENT_TEMPLATE,
      keywords: [],
      published: false
    });
    setShowEditor(true);
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      author: post.author,
      date: post.date,
      category: post.category,
      featured: post.featured,
      read_time: post.read_time,
      content: post.content,
      keywords: post.keywords,
      published: post.published
    });
    setShowEditor(true);
  };

  const handleSavePost = async () => {
    try {
      setSaving(true);

      if (!formData.title.trim() || !formData.content.trim()) {
        toast.error('Title and content are required');
        return;
      }

      const postData = {
        ...formData,
        updated_at: new Date().toISOString()
      };

      if (editingPost) {
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', editingPost.id);

        if (error) throw error;
        toast.success('Post updated successfully');
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert([{
            ...postData,
            created_at: new Date().toISOString()
          }]);

        if (error) throw error;
        toast.success('Post created successfully');
      }

      setShowEditor(false);
      setEditingPost(null);
      loadPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
      toast.success('Post deleted successfully');
      loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleTogglePublish = async (post: BlogPost) => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .update({ 
          published: !post.published,
          updated_at: new Date().toISOString()
        })
        .eq('id', post.id);

      if (error) throw error;

      setPosts(prev => prev.map(p => 
        p.id === post.id ? { ...p, published: !p.published } : p
      ));

      toast.success(post.published ? 'Post unpublished' : 'Post published');
    } catch (error) {
      console.error('Error toggling publish status:', error);
      toast.error('Failed to update publish status');
    }
  };

  const handleToggleFeatured = async (post: BlogPost) => {
    try {
      if (!post.featured && stats.featuredPosts >= 3) {
        toast.error('Maximum of 3 featured articles allowed');
        return;
      }

      const { error } = await supabase
        .from('blog_posts')
        .update({ 
          featured: !post.featured,
          updated_at: new Date().toISOString()
        })
        .eq('id', post.id);

      if (error) throw error;

      setPosts(prev => prev.map(p => 
        p.id === post.id ? { ...p, featured: !p.featured } : p
      ));

      toast.success(post.featured ? 'Removed from featured' : 'Added to featured');
    } catch (error) {
      console.error('Error toggling featured status:', error);
      toast.error('Failed to update featured status');
    }
  };

  const handleKeywordAdd = (keyword: string) => {
    if (keyword.trim() && !formData.keywords.includes(keyword.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keyword.trim()]
      }));
    }
  };

  const handleKeywordRemove = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.author.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    
    const matchesStatus = 
      selectedStatus === 'all' ||
      (selectedStatus === 'published' && post.published) ||
      (selectedStatus === 'draft' && !post.published) ||
      (selectedStatus === 'featured' && post.featured);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servet-blue"></div>
          <p className="text-bw-muted mt-4">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-bw-ink">Blog Management</h2>
          <p className="text-bw-muted mt-1">Create and manage blog content for your platform</p>
        </div>
        <div className="flex space-x-3">
          <button
            disabled
            title="Not yet available"
            className="flex items-center px-3 py-2 bg-bw-surface-subtle text-bw-muted rounded-md cursor-not-allowed opacity-70"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics <span className="ml-1.5 text-xs font-normal">(soon)</span>
          </button>
          <button
            onClick={handleNewPost}
            className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-bw-primary/90"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Post
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Total Posts</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.totalPosts}</p>
            </div>
            <BookOpen className="h-8 w-8 text-bw-muted" />
          </div>
        </div>

        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Published</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.publishedPosts}</p>
              <p className="text-xs text-bw-muted">Live on website</p>
            </div>
            <Globe className="h-8 w-8 text-success" />
          </div>
        </div>

        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Drafts</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.draftPosts}</p>
              <p className="text-xs text-bw-muted">Unpublished</p>
            </div>
            <Edit3 className="h-8 w-8 text-fit-moderate" />
          </div>
        </div>

        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Featured</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.featuredPosts}</p>
              <p className="text-xs text-bw-muted">Max 3 allowed</p>
            </div>
            <Star className="h-8 w-8 text-bw-primary" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-bw-muted" />
          <input
            type="text"
            placeholder="Search posts by title, excerpt, or author..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
        >
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Drafts</option>
          <option value="featured">Featured</option>
        </select>
        <button
          onClick={loadPosts}
          className="flex items-center px-3 py-2 bg-bw-surface-subtle text-bw-ink rounded-md hover:bg-gray-200"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </button>
      </div>

      {/* Posts Table */}
      <div className="bg-bw-surface shadow-card rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-bw-border">
          <thead className="bg-bw-surface-subtle">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Post
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Featured
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-bw-muted uppercase tracking-wider">
                Date
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-bw-surface divide-y divide-bw-border">
            {filteredPosts.map((post) => (
              <tr key={post.id} className="hover:bg-bw-surface-subtle">
                <td className="px-6 py-4">
                  <div className="flex items-start">
                    <FileText className="h-5 w-5 text-bw-muted mt-1 mr-3" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-bw-ink truncate">
                        {post.title}
                      </div>
                      <div className="text-sm text-bw-muted truncate">
                        {post.excerpt}
                      </div>
                      <div className="text-xs text-bw-muted mt-1">
                        by {post.author} • {post.read_time}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-bw-primary-tint text-bw-primary">
                    {post.category}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleTogglePublish(post)}
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      post.published
                        ? 'bg-success-bg text-success hover:bg-green-200'
                        : 'bg-fit-moderate-bg text-fit-moderate hover:bg-yellow-200'
                    }`}
                  >
                    {post.published ? 'Published' : 'Draft'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleFeatured(post)}
                    className={`p-1 rounded-full transition-colors ${
                      post.featured 
                        ? 'text-fit-moderate hover:text-fit-moderate' 
                        : 'text-bw-muted hover:text-bw-muted'
                    }`}
                    title={post.featured ? 'Remove from featured' : 'Add to featured'}
                  >
                    <Star className="h-4 w-4" fill={post.featured ? 'currentColor' : 'none'} />
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-bw-muted">
                  {new Date(post.date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditPost(post)}
                      className="text-bw-primary hover:text-bw-primary/80"
                      title="Edit post"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePost(post.id)}
                      className="text-urgent hover:text-urgent"
                      title="Delete post"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-bw-muted mx-auto mb-4" />
            <p className="text-bw-muted">
              {posts.length === 0 ? 'No blog posts yet' : 'No posts match your filters'}
            </p>
            {posts.length === 0 && (
              <button
                onClick={handleNewPost}
                className="mt-4 inline-flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-bw-primary/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create your first post
              </button>
            )}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative min-h-screen px-4 py-8">
            <div className="relative bg-bw-surface rounded-lg shadow-xl max-w-4xl mx-auto">
              {/* Modal Header */}
              <div className="flex justify-between items-center p-6 border-b border-bw-border">
                <h3 className="text-lg font-medium">
                  {editingPost ? 'Edit Post' : 'Create New Post'}
                </h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center px-3 py-1 text-sm bg-bw-surface-subtle rounded hover:bg-gray-200"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {showPreview ? 'Edit' : 'Preview'}
                  </button>
                  <button
                    onClick={() => setShowEditor(false)}
                    className="text-bw-muted hover:text-bw-muted"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {!showPreview ? (
                  <div className="space-y-6">
                    {/* Post Metadata */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-bw-ink">Title</label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                          placeholder="Enter post title..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-bw-ink">Category</label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                        >
                          {CATEGORIES.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-bw-ink">Excerpt</label>
                      <textarea
                        value={formData.excerpt}
                        onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                        rows={2}
                        className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                        placeholder="Brief description for search results and social media..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-bw-ink">Content</label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                        rows={20}
                        className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary font-mono text-sm"
                        placeholder="Write your post content in Markdown..."
                      />
                      <p className="mt-1 text-sm text-bw-muted">
                        Use Markdown formatting. Support for headers, lists, links, bold, italic, and more.
                      </p>
                    </div>

                    {/* Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-bw-ink">Author</label>
                        <input
                          type="text"
                          value={formData.author}
                          onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-bw-ink">Date</label>
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-bw-ink">Read Time</label>
                        <input
                          type="text"
                          value={formData.read_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, read_time: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                          placeholder="5 min read"
                        />
                      </div>
                    </div>

                    {/* Toggles */}
                    <div className="flex items-center space-x-6">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.published}
                          onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
                          className="rounded border-bw-border text-bw-primary shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                        />
                        <span className="ml-2 text-sm text-bw-ink">Publish immediately</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.featured}
                          onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                          className="rounded border-bw-border text-bw-primary shadow-sm focus:border-bw-primary focus:ring-bw-primary"
                          disabled={!formData.featured && stats.featuredPosts >= 3}
                        />
                        <span className="ml-2 text-sm text-bw-ink">
                          Featured post {stats.featuredPosts >= 3 && !formData.featured && '(Max 3)'}
                        </span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="prose max-w-none">
                    <h1>{formData.title}</h1>
                    <div className="text-bw-muted mb-4">
                      By {formData.author} • {new Date(formData.date).toLocaleDateString()} • {formData.read_time}
                    </div>
                    <div className="text-bw-ink mb-6">
                      {formData.excerpt}
                    </div>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {formData.content}
                    </ReactMarkdown>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end space-x-3 p-6 border-t border-bw-border">
                <button
                  onClick={() => setShowEditor(false)}
                  className="px-4 py-2 text-sm font-medium text-bw-ink bg-bw-surface-subtle rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePost}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-bw-primary rounded-md hover:bg-bw-primary/90 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}